import {
  Box,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Select,
  VStack,
} from '@chakra-ui/react';
import type { NextPage } from 'next';
import NextHead from 'next/head';
import router, { useRouter } from 'next/router';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { getPublicRuntimeConfig } from '../common/config';
import { sendAnalyticsEvent } from '../common/libs/googleAnalytics';
import { title } from '../common/libs/metadata';
import {
  SORT_ORDER_LIKE_COUNT_DESC,
  SORT_ORDER_SESSION_COUNT_DESC,
  SORT_ORDER_UPDATE_DATE_DESC,
  SortOrder,
} from '../common/libs/sortOrder';
import ArticlesGrid, { ArticleHeader } from '../components/ArticlesGrid';
import TargetReaderSelect from '../components/TargetReaderSelect';
import useApiClient from '../hooks/useApiClient';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import type { Props as ServerSideProps } from '../server/serverSideProps/articles';

export { getServerSideProps } from '../server/serverSideProps/articles';

function SortOrderComponent({
  sortOrder,
  readerTypeIds,
}: {
  sortOrder: SortOrder;
  readerTypeIds: number[];
}) {
  const router = useRouter();
  const onSelectChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const additionalQueryParam =
        readerTypeIds && readerTypeIds.length > 0
          ? `&readerTypeIds=${JSON.stringify(readerTypeIds)}`
          : '';
      router.push(
        `/articles?sortOrder=${e.target.value}${additionalQueryParam}`,
      );
    },
    [router, readerTypeIds],
  );

  return (
    <Flex justify="flex-end" wrap="wrap">
      <FormControl as={Flex} w="auto" align="center">
        <FormLabel m={0}>表示順：</FormLabel>
        <Select
          w="200px"
          fontSize="sm"
          value={sortOrder}
          onChange={onSelectChange}
          onFocus={() => sendAnalyticsEvent('slide', 'sort', null)}
        >
          <option value={SORT_ORDER_UPDATE_DATE_DESC}>
            公開日／更新日（新しい順）
          </option>
          <option value={SORT_ORDER_SESSION_COUNT_DESC}>
            ページ閲覧数（多い順）
          </option>
          <option value={SORT_ORDER_LIKE_COUNT_DESC}>
            保存済み数（多い順）
          </option>
        </Select>
      </FormControl>
    </Flex>
  );
}

function Articles({
  articles,
  isMobile,
}: {
  articles: ArticleHeader[];
  isMobile: boolean;
}) {
  if (articles.length > 0) {
    return (
      <ArticlesGrid
        articles={articles}
        gaEventLabel={'from_slides'}
        isMobile={isMobile}
        onShowThumbnail={(articleId) => {
          sendAnalyticsEvent(
            'slide_thumbnail',
            'show',
            `articles#${articleId}`,
          );
        }}
        onClick={(articleId) => {
          sendAnalyticsEvent(
            'slide_thumbnail',
            'click',
            `articles#${articleId}`,
          );
        }}
      />
    );
  } else {
    return (
      <Box w="100%">
        <Center>スライドはありません。</Center>
      </Box>
    );
  }
}

const ArticlesPage: NextPage<ServerSideProps> = ({
  articles,
  sortOrder,
  readerTypeIds,
  targetReaderTypes,
  isMobile,
}) => {
  const { apiClient } = useApiClient();

  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [renderArticles, setRenderArticles] = useState(articles);

  useEffect(() => {
    setRenderArticles(articles);
    setPage(1);
    setHasMore(true);
  }, [articles]);

  const targetReaderTypesForView = targetReaderTypes.map(
    (targetReaderType) => ({
      ...targetReaderType,
      selected: readerTypeIds.includes(targetReaderType.id),
    }),
  );

  const onTargetReadersChange = useCallback(
    (readerTypeIds: number[]) => {
      const additionalQueryParam =
        readerTypeIds.length > 0
          ? `&readerTypeIds=${JSON.stringify(readerTypeIds)}`
          : '';
      router.push(`/articles?sortOrder=${sortOrder}${additionalQueryParam}`);
    },
    [sortOrder],
  );

  const onClickTargetReaders = (event: ChangeEvent<HTMLInputElement>) => {
    const targetReaderId = Number.parseInt(event.target.id);
    let modifiedReaderTypeIds;
    if (readerTypeIds.includes(targetReaderId)) {
      modifiedReaderTypeIds = readerTypeIds.filter((s) => s !== targetReaderId);
    } else {
      modifiedReaderTypeIds = [...readerTypeIds, targetReaderId];
    }
    onTargetReadersChange(modifiedReaderTypeIds);
  };

  const loadMore = useCallback(async () => {
    if (isLoading || !hasMore) return;
    setIsLoading(true);
    const nextPage = page + 1;
    try {
      const res = await apiClient(
        `/api/articles?page=${nextPage}&readerTypeIds=${JSON.stringify(
          readerTypeIds,
        )}&sortOrder=${sortOrder}`,
        {
          method: 'GET',
        },
      );
      if (res.ok) {
        const data = await res.json();
        setPage(nextPage);
        setHasMore(data.hasMore);
        if (data.articles.length > 0) {
          setRenderArticles((v) => [...v, ...data.articles]);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [apiClient, hasMore, isLoading, page, readerTypeIds, sortOrder]);
  const intersectionRef = useIntersectionObserver(loadMore);

  const { slideWebBaseUrl } = getPublicRuntimeConfig();
  const canonicalUrl = `${slideWebBaseUrl}/articles`;

  return (
    <>
      <NextHead>
        <title>{title('全てのスライド')}</title>
        <link rel="canonical" href={canonicalUrl} />
      </NextHead>
      <VStack spacing={4} align="flex-start">
        <Heading as="h1" fontSize="2xl">
          全てのスライド
        </Heading>
        <Flex
          justify="space-between"
          w="full"
          wrap="wrap"
          alignItems="center"
          gap={2}
        >
          <TargetReaderSelect
            targetReaderTypes={targetReaderTypesForView}
            onChange={onClickTargetReaders}
          />
          <SortOrderComponent sortOrder={sortOrder} readerTypeIds={readerTypeIds} />
        </Flex>
        <Articles articles={renderArticles} isMobile={isMobile} />
        <div ref={intersectionRef} />
      </VStack>
    </>
  );
};

export default ArticlesPage;
